import { ProviderId } from 'firebase/auth';
import type { ValuesType, Nullish } from 'utility-types';

// Import helpers
import { sendGTMEvent } from './gtm';
import { convertPriceToNumberFormat } from 'helpers/prices';
import { getUserPacketsGtmData, convertGenresToBrands, getPacketsGTMData, getCancelledPackets } from './helpers';

// Import types
import type { TransactionDetails } from 'types/api/payments';
import type { PacketWithPriceAndChannels, SummaryProduct } from 'types/views/configurator';
import type { CurrentProductWithSubtype, UserSubscriptionParsed } from 'types/views/subscription';
import type { Genre } from 'types/api/products/product';
import {
	EventGTM,
	GTMSegmentation,
	GTMAction,
	GTMUserType,
	GtmPaymentStatus,
	GtmPaymentType,
	type ProductPaymentEventData,
	type SearchListParams,
	type EpgItemClickParams,
	type ChannelEventParams,
	type FilmEventParams,
	type ViewProductParams,
	type PacketEventCommonParams
} from 'types/views/gtm';

export const sendProductTransactionEvent = (details: TransactionDetails, status: GtmPaymentStatus) => {
	const { id, final_price, product_title, product_uuid, product_subtype } = details;

	const price = convertPriceToNumberFormat(final_price);

	sendGTMEvent({
		event: EventGTM.Purchase,
		data: {
			transaction_id: id,
			status,
			value: price,
			currency: 'PLN',
			items: [
				{
					item_id: product_uuid,
					item_name: product_title,
					currency: 'PLN',
					item_category: product_subtype,
					price,
					quantity: 1
				}
			]
		}
	});
};

export const sendPacketsTransactionEvent = (
	products: SummaryProduct[] = [],
	price: number,
	status: GtmPaymentStatus,
	transaction_id: number | null = null
) => {
	sendGTMEvent({
		event: EventGTM.Purchase,
		data: {
			transaction_id,
			status,
			currency: 'PLN',
			items: getPacketsGTMData(products),
			value: convertPriceToNumberFormat(price)
		}
	});
};

export const sendBannerEvent = (item_name: string, url = '') => {
	sendGTMEvent({
		event: EventGTM.ClickBanner,
		data: {
			segmentation: GTMSegmentation.Film,
			action: GTMAction.Click,
			page_url: window.location.href,
			url,
			item_name
		}
	});
};

export const sendFilmEvent = ({ item_name, item_variant, release_year, country, runtime, genres, label }: FilmEventParams) => {
	sendGTMEvent(
		{
			event: EventGTM.InteractWithFilm,
			data: {
				segmentation: GTMSegmentation.Film,
				action: GTMAction.Video,
				page_url: window.location.pathname,
				item_name,
				item_variant,
				release_year,
				country,
				runtime,
				label
			}
		},
		genres
	);
};

export const sendCarouselEvent = (url: string, label: string) => {
	sendGTMEvent({
		event: EventGTM.ClickCarousel,
		data: {
			segmentation: GTMSegmentation.Film,
			action: GTMAction.Click,
			page_url: window.location.href,
			url,
			label
		}
	});
};

export const sendChannelEvent = ({ url, genres, item_name }: ChannelEventParams) => {
	sendGTMEvent(
		{
			event: EventGTM.ClickTelevision,
			data: {
				segmentation: GTMSegmentation.Film,
				action: GTMAction.Click,
				page_url: window.location.href,
				url,
				item_name
			}
		},
		genres
	);
};

export const sendLoginEvent = (isExternalLogin: boolean, providerId: ValuesType<typeof ProviderId>) => {
	sendGTMEvent({
		event: EventGTM.Login,
		data: {
			segmentation: GTMSegmentation.Account,
			action: GTMAction.Form,
			user_type: isExternalLogin ? GTMUserType.External : GTMUserType.Vectra,
			method: isExternalLogin ? providerId : 'email'
		}
	});
};

export const sendSearchResultsEvent = ({ genres, ...rest }: SearchListParams) => {
	sendGTMEvent(
		{
			event: EventGTM.ClickSearchResults,
			data: {
				segmentation: GTMSegmentation.Film,
				action: GTMAction.Click,
				page_url: window.location.href,
				...rest
			}
		},
		genres
	);
};

export const sendSearchDropdownEvent = ({ genres, ...rest }: SearchListParams) => {
	sendGTMEvent(
		{
			event: EventGTM.ClickSearchList,
			data: {
				segmentation: GTMSegmentation.Film,
				action: GTMAction.Click,
				page_url: window.location.href,
				...rest
			}
		},
		genres
	);
};

export const sendEpgItemEvent = (params: EpgItemClickParams, genres: Genre[] | Nullish = null) => {
	sendGTMEvent(
		{
			event: EventGTM.ClickTvShow,
			data: {
				segmentation: GTMSegmentation.Film,
				action: GTMAction.Click,
				page_url: window.location.href,
				...params
			}
		},
		genres
	);
};

export const sendEpgFilterEvent = ({ url, label }: { url: string; label: string }) => {
	sendGTMEvent({
		event: EventGTM.ClickCarousel,
		data: {
			segmentation: GTMSegmentation.Film,
			action: GTMAction.Click,
			page_url: window.location.href,
			url,
			label
		}
	});
};

export const sendHeaderMenuEvent = ({ url, text }: { url: string; text: string }) => {
	sendGTMEvent({
		event: EventGTM.ClickHeaderMenu,
		data: {
			segmentation: GTMSegmentation.Global,
			action: GTMAction.Click,
			page_url: window.location.href,
			url,
			text
		}
	});
};

export const sendFaqEvent = (text: string) => {
	sendGTMEvent({
		event: EventGTM.SeeCtaFaq,
		data: {
			segmentation: GTMSegmentation.Global,
			action: GTMAction.Click,
			page_url: window.location.href,
			text
		}
	});
};

export const sendRegisterEvent = () => {
	sendGTMEvent({
		event: EventGTM.Register,
		data: {
			segmentation: GTMSegmentation.Account,
			action: GTMAction.Form
		}
	});
};

export const sendProductPaymentStartEvent = (data: ProductPaymentEventData) => {
	sendGTMEvent({
		event: EventGTM.PaymentStart,
		data
	});
};

export const sendProductAddToCartEvent = (data: ProductPaymentEventData) => {
	sendGTMEvent({
		event: EventGTM.AddToCart,
		data
	});
};

export const sendPacketRenewEvent = ({ product_uuid, name, subtype, price }: CurrentProductWithSubtype) => {
	sendGTMEvent({
		event: EventGTM.RenewPackage,
		data: {
			items: [
				{
					item_id: product_uuid,
					item_name: name,
					currency: 'PLN',
					item_brand: 'Vectra',
					item_category: `Pakiet ${subtype}`,
					price: convertPriceToNumberFormat(price),
					quantity: 1
				}
			]
		}
	});
};

export const sendRetryPaymentEvent = (params: { item_name: string; item_category: string }) =>
	sendGTMEvent({
		event: EventGTM.RetryPayment,
		data: { page_url: window.location.href, ...params }
	});

export const sendCancelPaymentEvent = (params: { item_name: string; item_category: string }) =>
	sendGTMEvent({
		event: EventGTM.CancelPayment,
		data: { page_url: window.location.href, ...params }
	});

export const sendProductViewEvent = ({ genres, price, ...params }: ViewProductParams) => {
	sendGTMEvent({
		event: EventGTM.ViewItem,
		data: {
			items: [
				{
					currency: 'PLN',
					item_category: 'VOD',
					quantity: 1,
					price: convertPriceToNumberFormat(price),
					...convertGenresToBrands(genres),
					...params
				}
			]
		}
	});
};

export const sendPacketCancelEvent = (currentSubscription: UserSubscriptionParsed | null, uuid: string) => {
	const packets = getCancelledPackets(currentSubscription, uuid);

	sendGTMEvent({
		event: EventGTM.CancelPackage,
		data: { items: getUserPacketsGtmData(packets) }
	});
};

export const sendPacketsViewEvent = (packets: PacketWithPriceAndChannels[]) =>
	sendGTMEvent({
		event: EventGTM.ViewItem,
		data: {
			items: getPacketsGTMData(packets)
		}
	});

export const sendPacketsPaymentStartEvent = (products: SummaryProduct[] = [], totalPrice: number | undefined) => {
	sendGTMEvent({
		event: EventGTM.PaymentStart,
		data: {
			currency: 'PLN',
			value: convertPriceToNumberFormat(totalPrice),
			items: getPacketsGTMData(products),
			payment_type: GtmPaymentType.CreditCard
		}
	});
};

export const sendBeginCheckoutEvent = (products: SummaryProduct[] = []) =>
	sendGTMEvent({
		event: EventGTM.BeginCheckout,
		data: {
			items: getPacketsGTMData(products)
		}
	});

export const sendRegulationsStepEvent = (products: SummaryProduct[] = []) =>
	sendGTMEvent({
		event: EventGTM.StepRegulation,
		data: {
			items: getPacketsGTMData(products)
		}
	});

export const sendDataStepEvent = (products: SummaryProduct[] = []) =>
	sendGTMEvent({
		event: EventGTM.StepData,
		data: {
			items: getPacketsGTMData(products)
		}
	});

export const sendPacketAddToCartEvent = ({ price, item_id, item_name, subtype }: PacketEventCommonParams) => {
	const readablePrice = convertPriceToNumberFormat(price);

	sendGTMEvent({
		event: EventGTM.AddToCart,
		data: {
			currency: 'PLN',
			value: readablePrice,
			items: [
				{
					item_id,
					item_name,
					currency: 'PLN',
					item_brand: 'Vectra',
					item_category: `Pakiet ${subtype}`,
					price: readablePrice,
					quantity: 1
				}
			]
		}
	});
};

export const sendPacketsLeaveCheckoutEvent = (products: SummaryProduct[] = [], totalPrice: number | undefined) =>
	sendGTMEvent({
		event: EventGTM.LeaveCheckout,
		data: {
			currency: 'PLN',
			value: convertPriceToNumberFormat(totalPrice),
			items: getPacketsGTMData(products)
		}
	});

export const sendProductLeaveCheckoutEvent = (data: ProductPaymentEventData) =>
	sendGTMEvent({
		event: EventGTM.LeaveCheckout,
		data
	});

export const sendPacketsPaymentInfoEvent = (products: SummaryProduct[] = [], totalPrice: number | undefined) =>
	sendGTMEvent({
		event: EventGTM.AddPaymentInfo,
		data: {
			currency: 'PLN',
			payment_type: GtmPaymentType.CreditCard,
			value: convertPriceToNumberFormat(totalPrice),
			items: getPacketsGTMData(products)
		}
	});
